import { createSlice } from '@reduxjs/toolkit';
import {
  getJobSearchesThunk,
  getOutreachCandidatesThunk,
  getSourcingCandidatesThunk,
  getSourcingJobsThunk,
} from '../thunks/FindCandidatesThunk';

/**
 * Find Candidates — sourced/outreach pipeline, modelled on InterviewManagementSlice.
 *
 * Deliberately its own slice rather than an extension of candidateManagementSlice: that
 * slice's tab state is shared with Screened Candidates and Interview Management, so
 * reusing it would make navigating between the screens clobber each other's tabs.
 */
const TAB_SUBTAB_MAP = {
  SOURCING: ['ALL', 'SAVED', 'SHORTLISTED'],
  OUTREACH: ['ALL', 'CONTACTED', 'REPLIED'],
};

export const SUBTAB_LABELS = {
  ALL: 'All',
  SAVED: 'Saved for Later',
  SHORTLISTED: 'Shortlisted',
  CONTACTED: 'Awaiting Reply',
  REPLIED: 'Replied',
};

export const subTabsFor = (tab) => TAB_SUBTAB_MAP[tab] ?? [];

/* ── Status refinement (the "Status :" select, as in candidateManagementSlice) ─
   Qualified / Not Qualified only mean something once someone has replied — an
   awaiting candidate has no submission to score — so they refine the Replied
   sub-tab rather than standing as sub-tabs of their own. */
const SUBSUBTAB_MAP = {
  OUTREACH: {
    REPLIED: ['All', 'Qualified', 'Not Qualified'],
  },
};

export const subSubTabsFor = (tab, subTab) => SUBSUBTAB_MAP[tab]?.[subTab] ?? [];

/* ── Sub-tab → list / count keys ─────────────────────────────────────────────
   One list per sub-tab, as InterviewManagementSlice keeps scheduled/inProgress/
   completed separate. The thunk echoes `filterby` back and the reducer files the
   rows into the matching list. */
const SOURCING_LIST = {
  ALL: 'sourcedCandidates',
  SAVED: 'savedCandidates',
  SHORTLISTED: 'shortlistedCandidates',
};
const OUTREACH_LIST = {
  ALL: 'outreachAllCandidates',
  CONTACTED: 'contactedCandidates',
  // Holds whatever the active Status refinement returned — All / Qualified /
  // Not Qualified are server-side filters of the same set, not separate lists.
  REPLIED: 'repliedCandidates',
};
const COUNT_KEY = {
  sourcedCandidates: 'sourcedCount',
  savedCandidates: 'savedCount',
  shortlistedCandidates: 'shortlistedCount',
  outreachAllCandidates: 'outreachAllCount',
  contactedCandidates: 'contactedCount',
  repliedCandidates: 'repliedCount',
};

const ALL_LISTS = [...Object.values(SOURCING_LIST), ...Object.values(OUTREACH_LIST)];

/** The list backing a given tab + sub-tab. */
export const listKeyFor = (tab, subTab) =>
  tab === 'OUTREACH' ? OUTREACH_LIST[subTab] : SOURCING_LIST[subTab];

/** Rows for the active sub-tab — the one selector the table reads. */
export const selectActiveCandidates = (state) => {
  const slice = state.findCandidatesSlice;
  return slice[listKeyFor(slice.activeTab, slice.activeSubTab)] ?? [];
};

/** A range counts as one filter once either end is set. Drives the icon's badge. */
export const selectFindFilterCount = (state) => {
  const { sourcedRange, status } = state.findCandidatesSlice.filters;
  return (sourcedRange?.[0] || sourcedRange?.[1] ? 1 : 0) + (status ? 1 : 0);
};

/** Sourced date and pipeline status only exist for sourced candidates — Outreach rows
 *  are keyed on their engagement instead. */
export const selectFiltersAvailable = (state) =>
  state.findCandidatesSlice.activeTab === 'SOURCING';

/* ── Tab persistence (mirrors candidateManagementSlice) ──────────────────────
   Keys are prefixed so they never collide with the ATS slice's own activeTab. */
// The tab itself is deliberately not restored: the page always opens on Sourcing, since
// that's where the pipeline starts. Only the sub-tabs remember where you were.
const getInitialTab = () => 'SOURCING';

const getInitialSubTab = (tab) => {
  const stored = localStorage.getItem('fcActiveSubTab');
  const valid = TAB_SUBTAB_MAP[tab];
  return stored && valid.includes(stored) ? stored : valid[0];
};

const getInitialSubSubTab = (tab, subTab) => {
  const stored = localStorage.getItem('fcActiveSubSubTab');
  const valid = subSubTabsFor(tab, subTab);
  return stored && valid.includes(stored) ? stored : 'All';
};

const initialTab = getInitialTab();
const initialSubTab = getInitialSubTab(initialTab);

const initialState = {
  activeTab: initialTab,
  activeSubTab: initialSubTab,
  activeSubSubTab: getInitialSubSubTab(initialTab, initialSubTab),

  // Data lists — one per sub-tab
  sourcedCandidates: [],
  savedCandidates: [],
  shortlistedCandidates: [],
  outreachAllCandidates: [],
  contactedCandidates: [],
  repliedCandidates: [],

  // Per-list totals, plus the active list's total for the pager
  sourcedCount: 0,
  savedCount: 0,
  shortlistedCount: 0,
  outreachAllCount: 0,
  contactedCount: 0,
  repliedCount: 0,
  totalCount: 0,

  currentPage: 1,
  pageSize: 10,
  loading: false,
  error: null,
  visitedSubTabs: {},

  // Job scope, owned by the URL: /find-candidates/:jobId. Null only while the picker
  // screen is up — the table route always carries a job, and syncs it in on mount.
  selectedJobId: null,
  selectedJobRole: '',
  jobRoles: [],
  // When jobRoles was last read from the server. The per-role counts are ORG-wide, so a
  // teammate's shortlisting changes them too — no client-side invalidation can be correct,
  // and the cache is bounded by age instead (see getSourcingJobsThunk's `condition`).
  jobRolesFetchedAt: null,
  jobsLoading: false,
  jobsError: null,

  // Search scope within the job — null is "All searches". Every list endpoint takes a
  // search_id, so this refines whichever tab is open rather than being a Sourcing-only
  // filter. The options are the job's own searches, reloaded whenever the job changes.
  selectedSearchId: null,
  searches: [],
  searchesLoading: false,

  searchQuery: '',
  debouncedSearch: '',

  // Date-window filters. Held as YYYY-MM-DD strings, not Date objects, so the store
  // stays serialisable; the calendar converts at the edges.
  showFilterDropdown: false,
  filters: {
    sourcedRange: [null, null],
    status: '',
  },

  selectedCandidates: [],
  selectionCount: 0,
  selectAllClicked: false,

  sortConfig: { key: null, direction: null },
};

/** Clear selection state — every tab/job/page/search change resets it. */
const resetSelection = (state) => {
  state.selectedCandidates = [];
  state.selectionCount = 0;
  state.selectAllClicked = false;
  ALL_LISTS.forEach((key) => {
    state[key].forEach((c) => {
      c.isSelected = false;
    });
  });
};

export const findCandidatesSlice = createSlice({
  name: 'findCandidatesSlice',
  initialState,
  reducers: {
    updateActiveTab: (state, action) => {
      const tab = action.payload;
      if (!TAB_SUBTAB_MAP[tab] || tab === state.activeTab) return;
      state.activeTab = tab;
      state.activeSubTab = TAB_SUBTAB_MAP[tab][0];
      state.activeSubSubTab = 'All';
      state.currentPage = 1;
      state.sortConfig = { key: null, direction: null };
      resetSelection(state);
      localStorage.setItem('fcActiveSubTab', TAB_SUBTAB_MAP[tab][0]);
      localStorage.setItem('fcActiveSubSubTab', 'All');
    },

    updateActiveSubTab: (state, action) => {
      const subTab = action.payload;
      if (!TAB_SUBTAB_MAP[state.activeTab].includes(subTab)) return;
      if (subTab === state.activeSubTab) return;
      state.activeSubTab = subTab;
      // Leaving Replied drops the Qualified/Not Qualified refinement — it has no
      // meaning on a tab where nobody has submitted an answer yet.
      state.activeSubSubTab = 'All';
      state.currentPage = 1;
      state.sortConfig = { key: null, direction: null };
      resetSelection(state);
      localStorage.setItem('fcActiveSubTab', subTab);
      localStorage.setItem('fcActiveSubSubTab', 'All');
    },

    updateActiveSubSubTab: (state, action) => {
      const subSubTab = action.payload;
      if (!subSubTabsFor(state.activeTab, state.activeSubTab).includes(subSubTab)) return;
      if (subSubTab === state.activeSubSubTab) return;
      state.activeSubSubTab = subSubTab;
      state.currentPage = 1;
      resetSelection(state);
      localStorage.setItem('fcActiveSubSubTab', subSubTab);
    },

    updateSelectedJob: (state, action) => {
      const { jobId, jobRole, searchId = null } = action.payload ?? {};
      const jobChanged = state.selectedJobId !== (jobId ?? null);
      state.selectedJobId = jobId ?? null;
      state.selectedJobRole = jobRole ?? '';
      state.currentPage = 1;
      // A search belongs to one job, so carrying the old one across roles would filter
      // every list down to nothing. Callers arriving on a specific search pass it here.
      if (jobChanged || searchId) {
        state.selectedSearchId = searchId;
        if (jobChanged) state.searches = [];
      }
      // Every list is job-scoped, so none of them survive a job change.
      state.visitedSubTabs = {};
      resetSelection(state);
    },

    /* Entering the screen through the job picker is the start of a new task, so the table
       opens on defaults: sub-tab, status refinement, date/status filters, name search,
       search scope and sort all go back to how a first-time visit would look.

       Without this the slice — which outlives the route, since nothing clears it on
       unmount — hands the next visit whatever narrowed the last one, and a job that looks
       empty because of a filter you didn't set this visit reads as missing candidates.

       Deliberately NOT wired to the header's job dropdown or the route-sync effect: those
       are mid-task moves inside the screen, where keeping your place is the point. */
    enterJobFromPicker: (state, action) => {
      const { jobId, jobRole } = action.payload ?? {};
      const tab = 'SOURCING';
      const subTab = TAB_SUBTAB_MAP[tab][0];

      // The sub-tabs are restored from localStorage at slice-init, so clearing the state
      // alone would let the old one reappear on the next app boot.
      localStorage.setItem('fcActiveSubTab', subTab);
      localStorage.setItem('fcActiveSubSubTab', 'All');

      return {
        ...initialState,
        // initialState's tabs are seeded from localStorage, which we just reset — spell
        // out the defaults rather than inheriting the values that were there at boot.
        activeTab: tab,
        activeSubTab: subTab,
        activeSubSubTab: 'All',
        // Neither of these describes the view: the dropdown's options would only flash
        // empty while /sourcing/jobs refetched, and rows-per-page is a density
        // preference that never hides a candidate.
        jobRoles: state.jobRoles,
        jobRolesFetchedAt: state.jobRolesFetchedAt,
        pageSize: state.pageSize,
        selectedJobId: jobId ?? null,
        selectedJobRole: jobRole ?? '',
      };
    },

    updateSelectedSearch: (state, action) => {
      const searchId = action.payload || null;
      if (state.selectedSearchId === searchId) return;
      state.selectedSearchId = searchId;
      state.currentPage = 1;
      // Every sub-tab's rows were fetched under the old scope.
      state.visitedSubTabs = {};
      resetSelection(state);
    },

    updateSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    // Set by the page's debounce timer — the thunks read this, never searchQuery, so a
    // request is never fired per keystroke.
    commitSearchQuery: (state, action) => {
      if (state.debouncedSearch === action.payload) return;
      state.debouncedSearch = action.payload;
      state.currentPage = 1;
      resetSelection(state);
    },

    updatePage: (state, action) => {
      state.currentPage = action.payload;
      resetSelection(state);
    },

    updatePageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
      resetSelection(state);
    },

    selectCandidateFromFindList: (state, action) => {
      const candidate = action.payload;
      const listKey = listKeyFor(state.activeTab, state.activeSubTab);
      const row = state[listKey].find((c) => c.candidate_id === candidate.candidate_id);
      if (!row) return;

      row.isSelected = !row.isSelected;
      if (row.isSelected) {
        state.selectedCandidates.push(row);
      } else {
        state.selectedCandidates = state.selectedCandidates.filter(
          (c) => c.candidate_id !== candidate.candidate_id,
        );
        state.selectAllClicked = false;
      }
      state.selectionCount = state.selectedCandidates.length;
    },

    // Select-all is page-scoped: the list holds the page the server returned, so this
    // reaches exactly the rows on screen.
    selectAllFindCandidates: (state, action) => {
      const value = action.payload;
      const listKey = listKeyFor(state.activeTab, state.activeSubTab);
      state.selectAllClicked = value;

      state[listKey].forEach((c) => {
        c.isSelected = value;
      });
      state.selectedCandidates = value ? state[listKey].map((c) => c) : [];
      state.selectionCount = state.selectedCandidates.length;
    },

    sortFindCandidatesByCol: (state, action) => {
      const { key, direction } = action.payload;
      state.sortConfig = { key, direction };
      if (!key || !direction) return; // reset — no sort applied

      const getValue = (candidate, col) => {
        switch (col) {
          case 'sourced_at':
          case 'saved_at':
          case 'initiated_at':
          case 'responded_at': {
            const date = Date.parse(candidate?.[col]);
            return isNaN(date) ? null : date;
          }
          default:
            return candidate?.[col]?.toString().toLowerCase() ?? null;
        }
      };

      const sortFn = (a, b) => {
        const valA = getValue(a, key);
        const valB = getValue(b, key);

        // Always push nulls to the bottom, in both directions.
        if (valA === null && valB === null) return 0;
        if (valA === null) return 1;
        if (valB === null) return -1;

        if (typeof valA === 'number') {
          return direction === 'asc' ? valA - valB : valB - valA;
        }
        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      };

      state[listKeyFor(state.activeTab, state.activeSubTab)].sort(sortFn);
    },

    // Optimistic row updates so a save/shortlist toggle doesn't need a full refetch.
    patchFindCandidate: (state, action) => {
      const { candidateId, changes } = action.payload;
      ALL_LISTS.forEach((key) => {
        const row = state[key].find((c) => c.candidate_id === candidateId);
        if (row) Object.assign(row, changes);
      });
    },

    removeFindCandidate: (state, action) => {
      const candidateId = action.payload;
      const listKey = listKeyFor(state.activeTab, state.activeSubTab);
      state[listKey] = state[listKey].filter((c) => c.candidate_id !== candidateId);
      state[COUNT_KEY[listKey]] = Math.max(0, state[COUNT_KEY[listKey]] - 1);
      state.totalCount = state[COUNT_KEY[listKey]];
      state.selectedCandidates = state.selectedCandidates.filter(
        (c) => c.candidate_id !== candidateId,
      );
      state.selectionCount = state.selectedCandidates.length;
    },

    /* Candidates who have left sourcing entirely — scheduling an interview hands them to
       Interview Management. Unlike removeFindCandidate this clears every list, not just
       the active one: the other sub-tabs are cached in visitedSubTabs and would otherwise
       still show the row when the user switches back to them. */
    removeFindCandidates: (state, action) => {
      const gone = new Set(action.payload ?? []);
      if (!gone.size) return;
      ALL_LISTS.forEach((key) => {
        const before = state[key].length;
        state[key] = state[key].filter((c) => !gone.has(c.candidate_id));
        const removed = before - state[key].length;
        if (removed) {
          state[COUNT_KEY[key]] = Math.max(0, state[COUNT_KEY[key]] - removed);
        }
      });
      state.totalCount = state[COUNT_KEY[listKeyFor(state.activeTab, state.activeSubTab)]];
      state.selectedCandidates = state.selectedCandidates.filter(
        (c) => !gone.has(c.candidate_id),
      );
      state.selectionCount = state.selectedCandidates.length;
      state.selectAllClicked = false;
    },

    resetVisitedSubTabs: (state) => {
      state.visitedSubTabs = {};
    },

    /* A search just added candidates, so the picker's Sourced counts and its
       latest-sourced order are out of date. Ages the cache out rather than clearing it:
       the picker's forced fetch then goes through even inside the TTL, while the header
       dropdown keeps its options instead of flashing empty. */
    invalidateJobRoles: (state) => {
      state.jobRolesFetchedAt = null;
    },

    toggleFindFilterDropdown: (state) => {
      state.showFilterDropdown = !state.showFilterDropdown;
    },

    // Applied as a set from the panel's Apply button, so a half-picked range never
    // triggers a fetch on its own.
    applyFindFilters: (state, action) => {
      const { sourcedRange, status } = action.payload ?? {};
      if (sourcedRange) state.filters.sourcedRange = sourcedRange;
      if (status !== undefined) state.filters.status = status;
      state.currentPage = 1;
      state.showFilterDropdown = false;
      resetSelection(state);
    },

    removeFindFilter: (state, action) => {
      const key = action.payload;
      if (key === 'SOURCED_RANGE') state.filters.sourcedRange = [null, null];
      if (key === 'STATUS') state.filters.status = '';
      state.currentPage = 1;
      resetSelection(state);
    },

    clearFindFilters: (state) => {
      state.filters = { sourcedRange: [null, null], status: '' };
      state.currentPage = 1;
      resetSelection(state);
    },

    clearFindCandidatesSlice: (state, action) => {
      const shouldClearJobRoles = action?.payload?.clearJobRoles || false;
      return {
        ...initialState,
        // The dropdown outlives a tab reset — refetching it on every tab change would
        // flash the job scope back to empty.
        jobRoles: shouldClearJobRoles ? [] : state.jobRoles,
        jobRolesFetchedAt: shouldClearJobRoles ? null : state.jobRolesFetchedAt,
      };
    },
  },

  extraReducers: (builder) => {
    /* ── Sourcing lists: ALL | SAVED | SHORTLISTED ─────────────────────────── */
    builder.addCase(getSourcingCandidatesThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSourcingCandidatesThunk.fulfilled, (state, action) => {
      const { candidates, total_count, filterby } = action.payload;
      const listKey = SOURCING_LIST[filterby];
      if (!listKey) return;

      state[listKey] = candidates;
      state[COUNT_KEY[listKey]] = total_count;
      state.totalCount = total_count;
      state.visitedSubTabs[`SOURCING_${filterby}`] = true;
      state.loading = false;
      state.error = null;

      // The result set can shrink under us (unsave, un-shortlist, a narrower search).
      // Without this the user is stranded on a page the server has no rows for;
      // currentPage is a refetch trigger, so correcting it reloads the last real page.
      const lastPage = Math.max(1, Math.ceil(total_count / state.pageSize));
      if (state.currentPage > lastPage) state.currentPage = lastPage;
    });
    builder.addCase(getSourcingCandidatesThunk.rejected, (state, action) => {
      // Superseded by a newer request (tab/job/page changed) — not a failure to report.
      if (action.meta?.aborted) return;
      state.loading = false;
      state.error = action.payload;
      console.error('Failed to fetch sourcing candidates:', action.payload);
    });

    /* ── Outreach lists: ALL | CONTACTED | REPLIED | QUALIFIED | NOT_QUALIFIED ─ */
    builder.addCase(getOutreachCandidatesThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getOutreachCandidatesThunk.fulfilled, (state, action) => {
      const { candidates, total_count, filterby } = action.payload;
      const listKey = OUTREACH_LIST[filterby];
      if (!listKey) return;

      state[listKey] = candidates;
      state[COUNT_KEY[listKey]] = total_count;
      state.totalCount = total_count;
      state.visitedSubTabs[`OUTREACH_${filterby}`] = true;
      state.loading = false;
      state.error = null;

      const lastPage = Math.max(1, Math.ceil(total_count / state.pageSize));
      if (state.currentPage > lastPage) state.currentPage = lastPage;
    });
    builder.addCase(getOutreachCandidatesThunk.rejected, (state, action) => {
      if (action.meta?.aborted) return;
      state.loading = false;
      state.error = action.payload;
      console.error('Failed to fetch outreach candidates:', action.payload);
    });

    /* ── Search scope picker ───────────────────────────────────────────────── */
    builder.addCase(getJobSearchesThunk.pending, (state) => {
      state.searchesLoading = true;
    });
    builder.addCase(getJobSearchesThunk.fulfilled, (state, action) => {
      state.searches = action.payload;
      state.searchesLoading = false;
      // A scope that no longer exists (deleted search, or one carried in from elsewhere)
      // would filter every list to empty with no way back — fall back to All searches.
      if (
        state.selectedSearchId &&
        !action.payload.some((s) => s.search_id === state.selectedSearchId)
      ) {
        state.selectedSearchId = null;
      }
    });
    builder.addCase(getJobSearchesThunk.rejected, (state, action) => {
      if (action.meta?.aborted) return;
      state.searchesLoading = false;
      console.error('Failed to fetch job searches:', action.payload);
    });

    /* ── Job-role picker + dropdown ────────────────────────────────────────── */
    builder.addCase(getSourcingJobsThunk.pending, (state) => {
      state.jobsLoading = true;
      state.jobsError = null;
    });
    builder.addCase(getSourcingJobsThunk.fulfilled, (state, action) => {
      state.jobRoles = action.payload;
      state.jobRolesFetchedAt = Date.now();
      state.jobsLoading = false;
      state.jobsError = null;
    });
    builder.addCase(getSourcingJobsThunk.rejected, (state, action) => {
      state.jobsLoading = false;
      state.jobsError = action.payload;
      console.error('Failed to fetch job roles:', action.payload);
    });
  },
});

export const {
  updateActiveTab,
  updateActiveSubTab,
  updateActiveSubSubTab,
  updateSelectedJob,
  enterJobFromPicker,
  updateSelectedSearch,
  updateSearchQuery,
  commitSearchQuery,
  updatePage,
  updatePageSize,
  selectCandidateFromFindList,
  selectAllFindCandidates,
  sortFindCandidatesByCol,
  patchFindCandidate,
  removeFindCandidate,
  removeFindCandidates,
  resetVisitedSubTabs,
  invalidateJobRoles,
  toggleFindFilterDropdown,
  applyFindFilters,
  removeFindFilter,
  clearFindFilters,
  clearFindCandidatesSlice,
} = findCandidatesSlice.actions;
